import { ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, forkJoin, fromEvent, merge, of } from 'rxjs';
import { debounceTime, share } from 'rxjs/operators';
import { I18nTranslator } from '../../../../i18n/translate';
import { AROrder } from '../../../../models/agrorendement-common.model';
import { Campaign } from '../../../../models/campaign.model';
import { Farm } from '../../../../models/farm.model';
import { Organisation } from '../../../../models/organisation.model';
import { Page } from '../../../../models/page.model';
import { ARGenericToolService, AROrderFilter, OrderSummary } from '../../../../services/agrorendement-common.service';
import { FarmService } from '../../../../services/farm.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ModalService } from '../../../../services/modal.service';
import { OrganisationService } from '../../../../services/organisation.service';
import { showErrorMessage, sortingString, toNull, toNumber } from '../../../../utils/utils';

export abstract class OrderComponent<C extends Campaign, O extends AROrder<any, any>> {
  // viewchild mis dans les classes concretes
  termFarmFilter: ElementRef;
  termCampaignFilter: ElementRef;
  termOrganisationFilter: ElementRef;
  allFilters: ElementRef;

  campaignFilter: number = null;
  farmFilter: number = null;
  organisationFilter: number = null;

  orders: O[] = [];
  page: number = 1;
  itemsPerPage: number = 10;
  maxSize: number = 5;
  numPages: number = 1;
  length: number = 0;

  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;

  orderSummary: OrderSummary = { area: 0, numberOfParcels: 0 };

  campaigns: C[];
  farms: Farm[];
  specy: string;

  showOrganisationFilter = false;
  organisations: Organisation[];

  // a redefinir dans les sous classes
  specyUrlPart: string[];
  specyString: string;
  specyOrderCode: string;

  // dans le constructeur de la sous-class (injectes)
  protected i18nTranslator: I18nTranslator;
  protected service: ARGenericToolService<C, O, any>;
  protected modal: ModalService;
  protected router: Router;
  protected farmService: FarmService;
  protected organisationService: OrganisationService;

  // static pour garder entre affichages de la liste
  static filterToApply: AROrderFilter;
  static allFiltersToShow: boolean;

  // a overrider
  abstract isOrderEditable(order: O): boolean;

  getOrdersPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: AROrderFilter) {
    this.service.getPageOrders(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter).subscribe(
      (page) => this.handlePage(page),
      (error) => this.handleError(error, this.i18nTranslator.translate('ar.order.error.load')),
    );
  }

  handlePage(page: Page<O>) {
    this.orders = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  ngOnInit() {
    const species = ['ble', 'colza', 'orge'];
    species.forEach((specy) => {
      if (this.router.url.includes(specy)) {
        this.specy = specy.toUpperCase();
      }
    });

    const obs1 = this.getFarms();
    const obs2 = this.getCampaigns();
    const obs3 = this.getOrganisations();

    forkJoin([obs1, obs2, obs3]).subscribe((data: Array<any>) => {
      if (!OrderComponent.filterToApply) {
        this.campaignFilter = this.campaigns[0].id;
        OrderComponent.filterToApply = this.buildFilter();
      }

      this.updateUIWithFilters();

      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    if (OrderComponent.filterToApply) {
      this.organisationFilter = toNumber(OrderComponent.filterToApply.organisationId);
      this.campaignFilter = toNumber(OrderComponent.filterToApply.campaignId);
      this.farmFilter = toNumber(OrderComponent.filterToApply.farmId);
    }

    if (OrderComponent.allFiltersToShow) {
      this.toggleAllFilters();
    }
  }

  ngAfterViewInit() {
    const observables = new Array<Observable<{}>>();
    const obs2 = fromEvent(this.termFarmFilter.nativeElement, 'change');
    const obs5 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
    observables.push(obs2, obs5);

    if (this.showOrganisationFilter) {
      const obs6 = fromEvent(this.termOrganisationFilter.nativeElement, 'change');
      observables.push(obs6);
    }

    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });
  }

  getCampaigns(): Observable<C[]> {
    const obs = this.service.getCampaigns().pipe(share());
    obs.subscribe((campaigns) => {
      this.campaigns = campaigns.sort(Campaign.compare).reverse();
    });
    return obs;
  }

  getFarms(): Observable<Farm[]> {
    const obs = this.farmService.getFarms().pipe(share());
    obs.subscribe((farms) => {
      this.farms = farms.sort(sortingString('name'));
    });
    return obs;
  }

  getOrganisations(): Observable<Organisation[]> {
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    if (isAdmin || isNegoce) {
      this.showOrganisationFilter = true;
      const obs = this.organisationService.getOrganisations().pipe(share());
      obs.subscribe((orgs) => {
        this.organisations = orgs.sort((a, b) => a.name.localeCompare(b.name));
      });
      return obs;
    } else {
      return of([]);
    }
  }

  getArea(filter: AROrderFilter) {
    this.service.getAreaForOrders(filter, this.specy).subscribe((orderSummary) => {
      this.orderSummary = orderSummary;
    });
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }, updateArea = true): any {
    const pageNumber = page.page;
    this.getOrdersPaginated(pageNumber, null, null, OrderComponent.filterToApply);
    // TODO ne pas appeler quand le filtre ne change pas ; pour l'instant fait que quand page === 1 car
    // dans updateFlters => pas de distinction entre clic sur 1 et mise a jour des filtres quand pas deja sur la page 1
    // TODO dans updateFlters suspendre le pageChanged pour toujours appeler le onChangeTable ?
    if (updateArea) {
      this.getArea(OrderComponent.filterToApply);
    }
  }

  clearFilters() {
    this.farmFilter = null;
    this.campaignFilter = null;
    this.organisationFilter = null;
    this.termFarmFilter.nativeElement.value = null;
    this.termCampaignFilter.nativeElement.value = null;
    if (this.showOrganisationFilter) {
      this.termOrganisationFilter.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    OrderComponent.filterToApply = this.buildFilter();

    // reset la page a la premiere et update la table
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du controle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  buildFilter(): AROrderFilter {
    const filter = {
      farmId: toNull(this.farmFilter),
      campaignId: toNull(this.campaignFilter),
      organisationId: toNull(this.organisationFilter),
    };
    return filter;
  }

  deleteOrder(order: O, event: any) {
    event.stopPropagation();

    this.modal.confirm(this.i18nTranslator.translate('ar.order.delete.confirm')).then((ok: boolean) => {
      if (ok) {
        this.service.deleteOrder(order).subscribe(
          (res: any) => {
            this.onChangeTable();
          },
          (error: any) =>
            this.handleError(error, this.i18nTranslator.translate('ar.order.error.delete', this.specyString)),
        );
      }
    });
  }

  editOrder(order: O, event: any) {
    this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(<string[]>['orders', 'edit', order.id]));
  }

  viewOrder(order: O, event: any) {
    this.router.navigate(['/orders'].concat(this.specyUrlPart).concat(<string[]>['orders', 'view', order.id]));
  }

  handleError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.order.error.unauthorized'));
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg);
    }
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      this.allFilters.nativeElement.style.display = 'none';
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      this.allFilters.nativeElement.style.display = 'block';
    }

    OrderComponent.allFiltersToShow = this.showAllFilters;
  }
}
